import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, RotateCcw } from "lucide-react";

// Shared network-state views (FR31): every screen that fetches renders one of
// loading (skeleton), error (inline role="alert" + retry) or empty (CTA).

export function ErrorState({
  message = "Algo não saiu como esperado. Tente novamente.",
  onRetry,
  testid = "error-state",
}) {
  return (
    <div
      role="alert"
      data-testid={testid}
      className="bg-brand-surface border border-brand-rose/40 rounded-3xl p-8 sm:p-10 text-center"
    >
      <div className="w-12 h-12 rounded-full bg-brand-rose/10 border border-brand-rose/40 flex items-center justify-center mx-auto mb-4">
        <AlertTriangle className="w-5 h-5 text-brand-rose" />
      </div>
      <p className="text-[14.5px] text-brand-ink mb-6 max-w-md mx-auto">{message}</p>
      {onRetry && (
        <button
          type="button"
          data-testid={`${testid}-retry`}
          onClick={onRetry}
          className="inline-flex items-center gap-2 bg-brand-rose hover:bg-brand-roseHover text-white px-6 py-3 rounded-full text-[13px] font-ui transition-colors min-h-[44px]"
        >
          <RotateCcw className="w-4 h-4" />
          Tentar de novo
        </button>
      )}
    </div>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  actionLabel,
  onAction,
  testid = "empty-state",
}) {
  return (
    <div
      data-testid={testid}
      className="bg-brand-surface border border-dashed border-brand-line rounded-3xl p-8 sm:p-12 text-center"
    >
      {Icon && <Icon className="w-8 h-8 text-brand-rose mx-auto mb-4" />}
      <h3 className="font-display text-2xl text-brand-ink mb-2">{title}</h3>
      {description && (
        <p className="text-[14px] text-brand-ink2 max-w-md mx-auto mb-6">{description}</p>
      )}
      {actionLabel && onAction && (
        <button
          type="button"
          data-testid={`${testid}-action`}
          onClick={onAction}
          className="inline-flex items-center gap-2 bg-brand-rose hover:bg-brand-roseHover text-white px-6 py-3 rounded-full text-[13px] font-ui transition-colors min-h-[44px]"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

// Generic responsive card-grid skeleton used by dashboard / library loading states.
export function CardGridSkeleton({ count = 6, testid = "loading-skeleton" }) {
  return (
    <div
      data-testid={testid}
      aria-busy="true"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5"
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="bg-brand-surface border border-brand-line rounded-2xl p-6"
        >
          <div className="flex items-center gap-4 mb-4">
            <Skeleton className="w-14 h-14 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </div>
          <Skeleton className="h-3 w-full mb-2" />
          <Skeleton className="h-3 w-5/6" />
        </div>
      ))}
    </div>
  );
}

export function ListSkeleton({ count = 4, testid = "loading-skeleton" }) {
  return (
    <div data-testid={testid} aria-busy="true" className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="bg-brand-surface border border-brand-line rounded-2xl p-5"
        >
          <Skeleton className="h-3 w-24 mb-2" />
          <Skeleton className="h-5 w-1/2" />
        </div>
      ))}
    </div>
  );
}
